// THE EXPLANATION SYSTEM, WEB SIDE.
//
// Every explanation is written once, in backend/explanations.py. This file only DISPLAYS
// it: the web fetches /api/explanations once, and the PDF and Excel read the same module
// directly. Nothing explanatory is written here, so there is no second copy to drift.
//
// Two tiers:
//   InfoMarker       a small marker beside a label. It opens IN PLACE to show the short
//                    form (four sentences at most, enforced by the backend) and closes on
//                    click. It links to the long form.
//   MethodologyPage  every topic in full, one page, anchored sections and a table of
//                    contents. It lives at /methodology, so a marker's link opens it in a
//                    new tab. There is no router: navigate() uses pushState, and usePath()
//                    follows popstate, which covers direct loads and the back button.
import { useRouteHead } from './head';
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { C, F, S, W, SP, R, BW } from './tokens';

export const METHODOLOGY_PATH = '/methodology';

// ---- NAVIGATION WITHOUT A ROUTER ----------------------------------------------------
export function navigate(path) {
  if (window.location.pathname + window.location.hash === path) return;
  window.history.pushState({}, '', path);
  // pushState does not fire popstate, so tell usePath() ourselves.
  window.dispatchEvent(new PopStateEvent('popstate'));
}

export function usePath() {
  const [path, setPath] = useState(window.location.pathname);
  useEffect(() => {
    const onPop = () => setPath(window.location.pathname);
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);
  return path;
}

// ---- THE CONTENT, FETCHED ONCE ------------------------------------------------------
const ExplanationsContext = createContext({ status: 'loading', groups: [], topics: {} });

export function ExplanationsProvider({ apiBase, children }) {
  const [state, setState] = useState({ status: 'loading', groups: [], topics: {}, order: [] });
  useEffect(() => {
    let live = true;
    fetch(`${apiBase}/api/explanations`)
      .then(r => r.json())
      .then(d => {
        if (!live) return;
        const topics = {};
        (d.topics || []).forEach(t => { topics[t.id] = t; });
        setState({ status: 'ready', groups: d.groups || [], topics,
                   order: (d.topics || []).map(t => t.id) });
      })
      .catch(() => { if (live) setState(s => ({ ...s, status: 'error' })); });
    return () => { live = false; };
  }, [apiBase]);
  return <ExplanationsContext.Provider value={state}>{children}</ExplanationsContext.Provider>;
}

export function useExplanations() {
  return useContext(ExplanationsContext);
}

// ---- TIER 1: THE MARKER -------------------------------------------------------------
// Renders nothing until the topic has loaded. A WRITTEN topic gets a button that opens the
// short form in place - a block-level span, so it can sit inside a <p> or a table cell and
// push the content below it down, which is what "in place" means. A STUB gets a plain link
// to its heading on the methodology page, because there is no short form to open.
export function InfoMarker({ id }) {
  const { topics } = useExplanations();
  const [open, setOpen] = useState(false);
  const toggle = useCallback((e) => { e.stopPropagation(); setOpen(o => !o); }, []);
  const topic = topics[id];
  if (!topic) return null;
  // A STUB HAS NOTHING TO EXPAND. Rather than render nothing - which left a mapped label
  // with no route to its topic at all - the marker becomes a plain link to the heading on
  // the methodology page. The page says the section is not written yet, which is the honest
  // answer, and the link is still the shortest way to it.
  if (topic.stub || !topic.short) {
    return (
      <a href={`${METHODOLOGY_PATH}#${id}`} target="_blank" rel="noopener noreferrer"
         title={`Read about ${topic.heading}`} aria-label={`Read about ${topic.heading}`}
         style={{ marginLeft: SP.xs, color: C.info, fontSize: S.micro, fontWeight: W.bold,
                  textDecoration: 'none' }}>
        ⓘ
      </a>
    );
  }
  return (
    <>
      <button type="button" onClick={toggle} aria-expanded={open}
              aria-label={`${open ? 'Hide' : 'Show'} the explanation: ${topic.heading}`}
              style={{ marginLeft: SP.xs, padding: 0, border: 'none', background: 'none',
                       color: C.info, fontSize: S.micro, fontWeight: W.bold, cursor: 'pointer',
                       fontFamily: F.ui, verticalAlign: 'baseline' }}>
        ⓘ
      </button>
      {open && (
        <span onClick={toggle} role="note"
              style={{ display: 'block', margin: `${SP.sm} 0`, padding: `${SP.sm} ${SP.md}`,
                       borderLeft: `${BW.thick} solid ${C.info}`, backgroundColor: C.surfaceAlt,
                       borderRadius: R.sm, color: C.textMuted, fontSize: S.small,
                       lineHeight: 1.6, cursor: 'pointer', textAlign: 'left',
                       fontWeight: W.normal }}>
          {topic.short}{' '}
          <a href={`${METHODOLOGY_PATH}#${id}`} target="_blank" rel="noopener noreferrer"
             onClick={e => e.stopPropagation()}
             style={{ color: C.accent, fontWeight: W.semi, textDecoration: 'none',
                      whiteSpace: 'nowrap' }}>
            Read the full explanation
          </a>
        </span>
      )}
    </>
  );
}

// ---- TIER 2: THE METHODOLOGY PAGE ---------------------------------------------------
export function MethodologyPage({ productName }) {
  const { status, groups, topics, order } = useExplanations();

  // A direct load of /methodology#net-debt arrives before the content does, so the
  // browser's own jump to the anchor finds nothing. Jump once the sections exist.
  useEffect(() => {
    if (status !== 'ready' || !window.location.hash) return;
    const el = document.getElementById(window.location.hash.slice(1));
    if (el) el.scrollIntoView();
  }, [status]);

  // G2. The title, the description and the canonical link, through the one helper every
  // route uses. This set only a title before, and nothing anywhere set a canonical.
  useRouteHead({
    title: `Methodology | ${productName}`,
    description: 'How Dealquire builds each figure: the valuation methods, the pro forma '
      + 'model, the synergy assumptions and the accounting treatment.',
    path: METHODOLOGY_PATH,
  });

  const byGroup = groups.map(g => ({ ...g, ids: order.filter(id => topics[id].group === g.id) }))
                        .filter(g => g.ids.length);

  const link = { color: C.textMuted, fontSize: S.small, textDecoration: 'none',
                 display: 'block', padding: `${SP.xxs} 0` };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: C.bg, color: C.text, fontFamily: F.ui,
                  padding: `${SP.xl} ${SP.xl} ${SP.max}` }}>
      <div style={{ maxWidth: '72rem', margin: '0 auto' }}>
        <div style={{ display: 'flex', gap: SP.lg, flexWrap: 'wrap' }}>
          <a href="/" onClick={e => { e.preventDefault(); navigate('/'); }}
             style={{ color: C.accent, fontSize: S.small, fontWeight: W.semi,
                      textDecoration: 'none' }}>
            Back to {productName}
          </a>
          {/* F1. The limitations page is linked from here as well as from the header: a reader
              on the methodology page is the reader most likely to want it. */}
          <a href="/limitations" onClick={e => { e.preventDefault(); navigate('/limitations'); }}
             style={{ color: C.textMuted, fontSize: S.small, fontWeight: W.semi,
                      textDecoration: 'none' }}>
            Limitations
          </a>
        </div>
        <h1 style={{ fontFamily: F.display, fontWeight: W.normal, fontSize: S.display,
                     margin: `${SP.lg} 0 ${SP.sm}` }}>Methodology</h1>
        <p style={{ color: C.textMuted, fontSize: S.body, lineHeight: 1.6,
                    margin: `0 0 ${SP.xxl}`, maxWidth: '48rem' }}>
          How every figure in {productName} is worked out, and where the data stops.
        </p>

        {status === 'loading' && <p style={{ color: C.textMuted, fontSize: S.body }}>Loading…</p>}
        {status === 'error' && (
          <p style={{ color: C.negative, fontSize: S.body }}>
            The methodology couldn't be loaded. Check that the server is running and reload.
          </p>
        )}

        {status === 'ready' && (
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 3fr)',
                        gap: SP.xxl, alignItems: 'start' }}>
            <nav aria-label="Contents"
                 style={{ position: 'sticky', top: SP.xl, maxHeight: '90vh', overflowY: 'auto' }}>
              <p style={{ color: C.textLabel, fontSize: S.small, fontWeight: W.semi,
                          margin: `0 0 ${SP.sm}` }}>Contents</p>
              {byGroup.map(g => (
                <div key={g.id} style={{ marginBottom: SP.lg }}>
                  <a href={`#group-${g.id}`} style={{ ...link, color: C.text, fontWeight: W.semi }}>
                    {g.heading}
                  </a>
                  {g.ids.map(id => (
                    <a key={id} href={`#${id}`}
                       style={{ ...link, paddingLeft: SP.md,
                                color: topics[id].stub ? C.textFaint : C.textMuted }}>
                      {topics[id].heading}
                    </a>
                  ))}
                </div>
              ))}
            </nav>

            <main>
              {byGroup.map(g => (
                <section key={g.id} id={`group-${g.id}`} style={{ marginBottom: SP.xxl }}>
                  <h2 style={{ fontFamily: F.display, fontWeight: W.normal, fontSize: S.h1,
                               color: C.text, margin: `0 0 ${SP.xl}`,
                               paddingBottom: SP.sm, borderBottom: `${BW.hair} solid ${C.line}` }}>
                    {g.heading}
                  </h2>
                  {g.ids.map(id => {
                    const t = topics[id];
                    return (
                      <article key={id} id={id} style={{ marginBottom: SP.xl, scrollMarginTop: SP.xl }}>
                        <h3 style={{ fontSize: S.h3, fontWeight: W.semi, margin: `0 0 ${SP.sm}`,
                                     color: t.stub ? C.textFaint : C.text }}>
                          <a href={`#${id}`} style={{ color: 'inherit', textDecoration: 'none' }}>
                            {t.heading}
                          </a>
                        </h3>
                        {t.stub ? (
                          <p style={{ color: C.textFaint, fontSize: S.small, margin: 0 }}>
                            Not written yet.
                          </p>
                        ) : (
                          <>
                            {/* THE THIRD STATE, 23-Sep-2026: the fact is written and the
                                fuller explanation of the idea is not. The page printed the
                                heading and nothing else for these, because it only ever read
                                the long form. It prints the fact and names what is missing. */}
                            {t.long_pending && (
                              <>
                                <p style={{ color: C.textMuted, fontSize: S.body,
                                            lineHeight: 1.7, margin: `0 0 ${SP.md}` }}>{t.short}</p>
                                <p style={{ color: C.textFaint, fontSize: S.small,
                                            margin: `0 0 ${SP.md}` }}>
                                  The longer explanation of this is not written yet.
                                </p>
                              </>
                            )}
                            {t.long.map((p, i) => (
                              <p key={i} style={{ color: C.textMuted, fontSize: S.body,
                                                  lineHeight: 1.7, margin: `0 0 ${SP.md}` }}>{p}</p>
                            ))}
                            {t.related.length > 0 && (
                              <p style={{ color: C.textFaint, fontSize: S.small, margin: 0 }}>
                                See also:{' '}
                                {t.related.map((r, i) => (
                                  <React.Fragment key={r}>
                                    {i > 0 && ', '}
                                    <a href={`#${r}`} style={{ color: C.accent, textDecoration: 'none' }}>
                                      {topics[r] ? topics[r].heading : r}
                                    </a>
                                  </React.Fragment>
                                ))}
                              </p>
                            )}
                          </>
                        )}
                      </article>
                    );
                  })}
                </section>
              ))}
            </main>
          </div>
        )}
      </div>
    </div>
  );
}
